// src/hooks/useSalonData.ts
import { useState, useEffect } from "react";
import { StylingStaff, Category, SelectProduct } from "../types/salon.types";
import { getCategory, getProduct, getStaff } from "src/shared/services/servicesv2";

export const useSalonData = () => {
  const [staffMembers, setStaffMembers] = useState<StylingStaff[]>([]);
  const [products, setProducts] = useState<SelectProduct[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loadingStates, setLoadingStates] = useState({
    staff: true,
    categories: true,
    products: true
  });
  const [error, setError] = useState<string | null>(null);
  const [reFresh, setReset] = useState<boolean>(false);

  // Computed loading state - true if any individual loading is true
  const loading = loadingStates.staff || loadingStates.categories || loadingStates.products;

  const fetchStaff = async () => {
    try {
      setLoadingStates(prev => ({ ...prev, staff: true }));
      const response = await getStaff();
      setStaffMembers(response.data.result);
    } catch (err) {
      setError("Failed to fetch staff members");
      console.error("API Error:", err);
    } finally {
      setLoadingStates(prev => ({ ...prev, staff: false }));
    }
  };

  const fetchCategories = async () => {
    try {
      setLoadingStates(prev => ({ ...prev, categories: true }));
      const response = await getCategory();
      setCategories(response.data.categoryDetails);
    } catch (err) {
      setError("Failed to fetch categories");
      console.error("API Error:", err);
    } finally {
      setLoadingStates(prev => ({ ...prev, categories: false }));
    }
  };

  const fetchProducts = async () => {
    try {
      setLoadingStates(prev => ({ ...prev, products: true }));
      const response = await getProduct();
      setProducts(response.data.results);
    } catch (err) {
      setError("Failed to fetch products");
      console.error("Error:", err);
    } finally {
      setLoadingStates(prev => ({ ...prev, products: false }));
    }
  };

  // Fetch all data in parallel
  const fetchAllData = async () => {
    setError(null);
    // Reset loading states
    setLoadingStates({
      staff: true,
      categories: true,
      products: true
    });

    // Execute all promises but handle errors individually
    await Promise.allSettled([
      fetchStaff(),
      fetchCategories(),
      fetchProducts()
    ]);
  };

  // Fetch all data on mount and when refresh changes
  useEffect(() => {
    fetchAllData();
  }, [reFresh]);

  // 🔁 Toggle refresh handler
  const toggleRefresh = () => setReset(prev => !prev);

  return {
    staffMembers,
    products,
    categories,
    loading, // Will be true until all three complete
    error,
    loadingStates, // Optional: expose individual states if needed
    refetchStaff: fetchStaff,
    refetchCategories: fetchCategories,
    refetchProducts: fetchProducts,
    refetchAll: fetchAllData,
    toggleRefresh,
  };
};